import React from 'react';
import { Box, CssBaseline, ThemeProvider } from '@mui/material';
import { Link, Outlet, useParams } from 'react-router-dom';
import { signalTheme } from '@/theme/signalTheme';
import {
  signalBorders,
  signalFonts,
  signalRadii,
  signalStatus,
  signalSurfaces,
  signalText,
} from '@/theme/foundations/tokens';
import { Mono } from '@/common/components/signal';
import { signalScreens } from './screens/registry';

/**
 * Frame around every Signal surface: applies the Signal theme in isolation from
 * `AppThemeProvider`, paints the canvas, and pins a rail for jumping between
 * screens while the redesign is under review.
 */
export const SignalLayout: React.FC = () => {
  const { screenId } = useParams();

  return (
    <ThemeProvider theme={signalTheme}>
      <CssBaseline />
      <Box
        sx={{
          minHeight: '100vh',
          background: signalSurfaces.canvas,
          color: signalText.primary,
          fontFamily: signalFonts.ui,
          display: 'flex',
        }}
      >
        <Box
          component="nav"
          aria-label="Signal screens"
          sx={{
            width: 210,
            flex: 'none',
            borderRight: `1px solid ${signalBorders.divider}`,
            background: signalSurfaces.panel,
            padding: '20px 12px',
            display: 'flex',
            flexDirection: 'column',
            gap: '3px',
            position: 'sticky',
            top: 0,
            alignSelf: 'flex-start',
            maxHeight: '100vh',
            overflowY: 'auto',
          }}
        >
          <Box
            component={Link}
            to="/signal"
            sx={{
              textDecoration: 'none',
              padding: '6px 10px 14px',
              display: 'block',
            }}
          >
            <Mono
              size={10}
              weight={600}
              letterSpacing="0.14em"
              color={signalStatus.syncing.fg}
            >
              SIGNAL SYSTEM
            </Mono>
          </Box>

          {signalScreens.map((screen) => {
            const active = screen.id === screenId;
            const built = Boolean(screen.Component);

            return (
              <Box
                key={screen.id}
                component={Link}
                to={`/signal/${screen.id}`}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '9px',
                  padding: '8px 10px',
                  borderRadius: `${signalRadii.md}px`,
                  textDecoration: 'none',
                  fontSize: '12.5px',
                  fontWeight: active ? 600 : 500,
                  color: active ? signalText.primary : signalText.secondary,
                  background: active ? signalStatus.syncing.bg : 'transparent',
                  '&:hover': {
                    background: active
                      ? signalStatus.syncing.bg
                      : signalSurfaces.fillStrong,
                    color: signalText.primary,
                  },
                }}
              >
                <Mono
                  size={10}
                  weight={600}
                  color={active ? signalStatus.syncing.fg : signalText.faint}
                >
                  {screen.index}
                </Mono>
                <Box
                  component="span"
                  sx={{
                    flex: 1,
                    minWidth: 0,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {screen.title}
                </Box>
                {/* A dot marks a screen that has not been implemented yet. */}
                {!built && (
                  <Box
                    component="span"
                    aria-label="not built yet"
                    sx={{
                      width: 5,
                      height: 5,
                      flex: 'none',
                      borderRadius: '999px',
                      background: signalText.faint,
                    }}
                  />
                )}
              </Box>
            );
          })}
        </Box>

        <Box sx={{ flex: 1, minWidth: 0, overflowX: 'auto' }}>
          <Outlet />
        </Box>
      </Box>
    </ThemeProvider>
  );
};

export default SignalLayout;
